import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

/**
 * Full-screen video modal for post-will completion
 * Features:
 * - Dark overlay background
 * - Centered YouTube video with no visible frame
 * - Restricted YouTube embed to prevent easy link access
 * - Right-click disabled
 * - Responsive design
 */
const WillCompletionVideoModal = ({ isOpen, onClose, videoUrl, videoTitle }) => {
  const modalRef = useRef(null);
  const iframeContainerRef = useRef(null);

  // Extract YouTube video ID from various URL formats
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const videoId = getYouTubeVideoId(videoUrl);

  // Build restricted YouTube embed URL
  const getRestrictedEmbedUrl = (id) => {
    if (!id) return null;
    const params = new URLSearchParams({
      autoplay: '1',           // Autoplay when modal opens
      controls: '0',           // Hide controls
      disablekb: '1',          // Disable keyboard controls
      fs: '0',                 // Disable fullscreen button
      modestbranding: '1',     // Minimal YouTube branding
      rel: '0',                // No related videos at end
      showinfo: '0',           // Hide video info (deprecated but included)
      iv_load_policy: '3',     // Hide annotations
      playsinline: '1',        // Play inline on mobile
      enablejsapi: '0',        // Disable JS API
      origin: window.location.origin
    });
    return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
  };

  // Prevent right-click on video container
  useEffect(() => {
    const container = iframeContainerRef.current;
    if (!container) return;

    const preventContext = (e) => {
      e.preventDefault();
      return false;
    };

    container.addEventListener('contextmenu', preventContext);
    return () => container.removeEventListener('contextmenu', preventContext);
  }, [isOpen]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !videoId) return null;

  const embedUrl = getRestrictedEmbedUrl(videoId);

  return (
    <div 
      ref={modalRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ top: 0, left: 0, right: 0, bottom: 0, position: 'fixed' }}
      data-testid="will-completion-video-modal"
    >
      {/* Blurred Background Overlay - Light blur */}
      <div 
        className="absolute inset-0 bg-slate-900/85 backdrop-blur-[2px]"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        onClick={onClose}
      />
      
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 z-[10000] w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200"
        style={{ position: 'fixed' }}
        data-testid="video-modal-close-btn"
        aria-label="Close video"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Video Container */}
      <div 
        ref={iframeContainerRef}
        className="relative z-[10000] w-full max-w-5xl mx-4 aspect-video select-none rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10"
        style={{ 
          pointerEvents: 'auto',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Invisible overlay to block clicks on YouTube logo/links */}
        <div 
          className="absolute inset-0 z-20 pointer-events-none"
          style={{
            background: 'transparent'
          }}
        />
        
        {/* Top overlay to hide YouTube logo area */}
        <div 
          className="absolute top-0 left-0 right-0 h-12 z-10 pointer-events-auto cursor-default"
          onClick={(e) => e.stopPropagation()}
        />
        
        {/* Bottom overlay to hide controls/watermark */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-16 z-10 pointer-events-auto cursor-default bg-gradient-to-t from-slate-900/80 to-transparent"
          onClick={(e) => e.stopPropagation()}
        />

        {/* YouTube Iframe */}
        <iframe
          src={embedUrl}
          title={videoTitle || "Will Completion Video"}
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          sandbox="allow-scripts allow-same-origin allow-presentation"
          style={{
            pointerEvents: 'auto'
          }}
        />
      </div>

      {/* Continue Button */}
      <div className="fixed bottom-8 left-0 right-0 flex justify-center z-[10000]">
        <button
          onClick={onClose}
          className="px-8 py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded-full transition-all duration-200 shadow-lg hover:shadow-xl"
          data-testid="video-modal-continue-btn"
        >
          Continue to Dashboard
        </button>
      </div>
    </div>
  );
};

export default WillCompletionVideoModal;
